import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { filter, Observable } from 'rxjs';
import { ConfigurationService } from '../../services/configuration.service';

import {
  CreateWorkspaceRequest,
  CreateWorkspaceResponse,
  EditWorkspaceRequest,
  GetWorkspacesQueryParams,
  Workspace
} from '../models/workspaces';
import { DriftConfiguration } from 'src/app/models/configuration.interface';

@Injectable()
export class WorkspaceService {
  private readonly workspaceUrl = 'workspace';
  private config!: DriftConfiguration;
  constructor(
    private readonly httpClient: HttpClient,
    private readonly configurationService: ConfigurationService
  ) {
    //No unsub required
    this.configurationService
      .configurationLoaded()
      .pipe(filter(loaded => loaded === true))
      .subscribe(() => {
        this.config = this.configurationService.getConfiguration();
      });
  }

  private get apiUrl() {
    return `${this.config.driftApi}/${this.workspaceUrl}`;
  }

  public getWorkspaces(params?: GetWorkspacesQueryParams): Observable<Workspace[]> {
    let httpParams = new HttpParams();
    if (params?.include_archived !== undefined) {
      httpParams = httpParams.set('include_archived', params.include_archived);
    }
    if (params?.include_deleted !== undefined) {
      httpParams = httpParams.set('include_deleted', params.include_deleted);
    }
    return this.httpClient.get<Workspace[]>(this.apiUrl, { params: httpParams });
  }

  public createWorkspace(data: CreateWorkspaceRequest): Observable<CreateWorkspaceResponse> {
    return this.httpClient.post<CreateWorkspaceResponse>(this.apiUrl, data);
  }

  public getWorkspaceDetails(workspaceId: string): Observable<Workspace> {
    const path = `${this.apiUrl}/${workspaceId}`;
    return this.httpClient.get<Workspace>(path);
  }

  public editWorkspace(workspaceId: string, data: EditWorkspaceRequest): Observable<void> {
    const path = `${this.apiUrl}/${workspaceId}`;
    return this.httpClient.put<void>(path, data);
  }

  public archiveWorkspace(workspaceId: string): Observable<void> {
    const path = `${this.apiUrl}/${workspaceId}/archive`;
    return this.httpClient.post<void>(path, {});
  }

  public unarchiveWorkspace(workspaceId: string): Observable<void> {
    const path = `${this.apiUrl}/${workspaceId}/unarchive`;
    return this.httpClient.post<void>(path, {});
  }

  public deleteWorkspace(workspaceId: string): Observable<void> {
    const path = `${this.apiUrl}/${workspaceId}`;
    return this.httpClient.delete<void>(path);
  }
  public acceptInvitation(workspaceId: string, token: string): Observable<void> {
    const path = `${this.apiUrl}/${workspaceId}/accept`;
    const params = new HttpParams().set('token', token);
    return this.httpClient.get<void>(path, { params: params });
  }

  public submitForReview(workspaceId: string): Observable<{ message: string }> {
    const path = `${this.apiUrl}/${workspaceId}/submit-for-review`;
    return this.httpClient.post<{ message: string }>(path, {});
  }

  public withdrawToDrafting(workspaceId: string): Observable<{ message: string }> {
    const path = `${this.apiUrl}/${workspaceId}/withdraw-to-drafting`;
    return this.httpClient.post<{ message: string }>(path, {});
  }

  public approveWorkspace(workspaceId: string): Observable<{ message: string }> {
    const path = `${this.apiUrl}/${workspaceId}/approve`;
    return this.httpClient.post<{ message: string }>(path, {});
  }

  public finalizeWorkspace(workspaceId: string): Observable<{ message: string }> {
    const path = `${this.apiUrl}/${workspaceId}/finalize`;
    return this.httpClient.post<{ message: string }>(path, {});
  }
}
